import { z } from 'zod';
import { paginationMetaSchema } from '../common/pagination';
import { moneySchema } from '../money';
import { projectStatusSchema } from '../projects/project.schema';
import { salesPipelineEntryResponseSchema } from '../sales/pipeline.schema';

/**
 * Rolling Forecast (ADR 0056): erwarteter Umsatz je Monat aus zwei Quellen —
 * aktive Projekte (voller Auftragswert im Monat von `plannedEndDate`) und die
 * Vertriebspipeline (Volumen × Auftragswahrscheinlichkeit im Monat des
 * erwarteten Auftragseingangs). Reine Read-Aggregation im Modul reporting.
 */
export const rollingForecastProjectSchema = z.object({
  projectId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  legalEntityName: z.string().nullable(),
  status: projectStatusSchema,
  orderValue: moneySchema.nullable(),
  /**
   * Plan-Ende des Projekts — im Rolling Forecast als "Fertigstellung"
   * editierbar (Amendment 2026-07-21: kein eigenes Forecast-Feld mehr, um
   * doppelte Datenpflege zu vermeiden; siehe ADR 0056).
   */
  plannedEndDate: z.string().datetime().nullable(),
});
export type RollingForecastProject = z.infer<typeof rollingForecastProjectSchema>;

/** Monat als 'JJJJ-MM' — der Schlüssel, in dem der Forecast rechnet. */
const monthKeySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Erwartet JJJJ-MM');

/**
 * Sonderwert des Gesellschafts-Filters: NUR Datensätze ohne zugeordnete
 * Gesellschaft. Braucht es, weil `ownerLegalEntityId` an den meisten Projekten
 * (noch) leer ist — „ohne Zuordnung" ist damit eine echte Auswertungsgruppe und
 * nicht bloß ein Randfall.
 */
export const FORECAST_NO_LEGAL_ENTITY = 'none';

/**
 * Boolean aus einem Query-String. Bewusst `'true'|'false'` OHNE `.transform`:
 * `z.coerce.boolean()` macht aus dem String „false" ein `true`, und ein
 * `transform` würde Ein-/Ausgabetyp trennen, was die `ZodValidationPipe` nicht
 * annimmt (Muster: `analytics.schema.ts`).
 */
const booleanQuerySchema = z.enum(['true', 'false']);

/**
 * Filter, die für ALLE Forecast-Sichten gleich gelten (Monatsübersicht,
 * Gesellschaft × Geschäftsjahr, Auftragsliste, Pipeline) — deshalb einmal
 * definiert und in beide Query-Schemas gespreadet.
 */
const forecastFilterShape = {
  /** Erster Monat des Horizonts (inkl.); ohne Angabe ab dem frühesten Datensatz. */
  from: monthKeySchema.optional(),
  /** Letzter Monat des Horizonts (inkl.); ohne Angabe bis zum spätesten Datensatz. */
  to: monthKeySchema.optional(),
  /** Gesellschaft (UUID) oder `none` für „ohne Zuordnung"; ohne Angabe: alle. */
  legalEntityId: z.union([z.string().uuid(), z.literal(FORECAST_NO_LEGAL_ENTITY)]).optional(),
  /** Pipeline mitrechnen (Vorgabe: ja). Aus = reiner Auftragsbestand. */
  includePipeline: booleanQuerySchema.optional(),
  /** Nur Pipeline-Einträge ab dieser Auftragswahrscheinlichkeit (0–100). */
  minProbabilityPct: z.coerce.number().int().min(0).max(100).optional(),
};

/** Ein umgedrehter Zeitraum ergäbe einen leeren Horizont — lieber abweisen. */
const isMonthRangeOrdered = (value: { from?: string; to?: string }): boolean =>
  !value.from || !value.to || value.from <= value.to;
const MONTH_RANGE_ERROR = {
  message: 'Der Startmonat darf nicht nach dem Endmonat liegen.',
  path: ['to'],
};

/** Query der Forecast-Übersicht (Monate, Gesellschaft × Jahr, Pipeline). */
export const rollingForecastQuerySchema = z
  .object(forecastFilterShape)
  .refine(isMonthRangeOrdered, MONTH_RANGE_ERROR);
export type RollingForecastQuery = z.infer<typeof rollingForecastQuerySchema>;

/**
 * Sortierung der Auftragsliste. `month` beantwortet die Frage „welcher Auftrag
 * landet in welchem Monat" (Fertigstellung aufsteigend, Projekte ohne Termin
 * zuletzt), `code` ist die gewohnte Reihenfolge nach Projektnummer.
 */
export const rollingForecastSortSchema = z.enum(['code', 'month']);
export type RollingForecastSort = z.infer<typeof rollingForecastSortSchema>;

/** Eine Monatszeile des Umsatz-Forecasts (YYYY-MM), je Quelle + Summe. */
export const rollingForecastMonthSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  label: z.string(), // z. B. 'Aug 2026'
  activeProjects: moneySchema, // voller Auftragswert je Plan-Ende-Monat
  pipelineWeighted: moneySchema, // Volumen × Wahrscheinlichkeit je Auftragseingangs-Monat
  total: moneySchema,
});
export type RollingForecastMonth = z.infer<typeof rollingForecastMonthSchema>;

/**
 * Ein Geschäftsjahr einer Gesellschaft. Geschäftsjahr = Kalenderjahr — so
 * rechnet das System durchgehend (Auftragseingang, Umsatz, Belegfilter);
 * ein abweichendes Geschäftsjahr gibt es nirgends.
 */
export const rollingForecastYearCellSchema = z.object({
  year: z.number().int(),
  activeProjects: moneySchema,
  pipelineWeighted: moneySchema,
  total: moneySchema,
});
export type RollingForecastYearCell = z.infer<typeof rollingForecastYearCellSchema>;

/** Eine Zeile der Kreuztabelle: eine Gesellschaft über alle Geschäftsjahre. */
export const rollingForecastEntityRowSchema = z.object({
  /** `null` = Projekte/Pipeline-Einträge ohne zugeordnete Gesellschaft. */
  legalEntityId: z.string().uuid().nullable(),
  legalEntityName: z.string().nullable(),
  /** Je Geschäftsjahr eine Zelle, lückenlos über `years` der Antwort. */
  years: z.array(rollingForecastYearCellSchema),
  /** Zeilensumme über alle Geschäftsjahre. */
  total: moneySchema,
});
export type RollingForecastEntityRow = z.infer<typeof rollingForecastEntityRowSchema>;

/**
 * Der eigentliche Report: Umsatz je Monat, je Gesellschaft/Geschäftsjahr und
 * die Pipeline. Alles serverseitig über ALLE aktiven Projekte + die gesamte
 * Pipeline gerechnet (günstige Aggregation über wenige Spalten) — Geldbeträge
 * werden nie im Browser addiert (§6). Die (potenziell große) Liste der aktiven
 * Projekte kommt bewusst NICHT hier mit, sondern separat paginiert
 * (`rollingForecastActiveProjectsPageSchema`).
 */
export const rollingForecastResponseSchema = z.object({
  currency: z.enum(['EUR', 'USD', 'CHF']),
  /** Vertriebspipeline (voraussichtliche Projekte) — gefiltert wie der Report. */
  pipeline: z.array(salesPipelineEntryResponseSchema),
  /** Umsatz-Forecast je Monat, chronologisch. */
  months: z.array(rollingForecastMonthSchema),
  /** Geschäftsjahre im gefilterten Horizont, aufsteigend. */
  years: z.array(z.number().int()),
  /** Kreuztabelle Gesellschaft × Geschäftsjahr, größte Gesamtsumme zuerst. */
  byLegalEntity: z.array(rollingForecastEntityRowSchema),
  /** Spaltensummen der Kreuztabelle je Geschäftsjahr (Fußzeile). */
  totalsByYear: z.array(rollingForecastYearCellSchema),
  /**
   * Aktive Projekte ohne Fertigstellung: Sie tauchen in KEINEM Monat auf und
   * fehlen damit im Forecast. Die Zahl steht bewusst im Report — sonst sieht
   * niemand, wie belastbar er überhaupt ist. Unabhängig vom Zeitraum-Filter.
   */
  undatedActiveProjects: z.number().int(),
  /** Summen über den gesamten Horizont. */
  totals: z.object({
    activeProjects: moneySchema,
    pipelineWeighted: moneySchema,
    total: moneySchema,
  }),
});
export type RollingForecastResponse = z.infer<typeof rollingForecastResponseSchema>;

/**
 * Listen-Query für die paginierte Tabelle der aktiven Projekte (Fertigstellung).
 * Trägt dieselben Filter wie der Report — sonst zeigte die Liste unter einer
 * gefilterten Monatstabelle andere Aufträge, als die Summen darüber behaupten.
 *
 * Mit gesetztem Zeitraum fallen Projekte OHNE Fertigstellung heraus: Sie liegen
 * in keinem Monat. Ohne Zeitraum-Filter sind sie dabei (und ihre Zahl steht
 * zusätzlich als `undatedActiveProjects` im Report).
 */
export const rollingForecastActiveQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(25),
    sort: rollingForecastSortSchema.optional(),
    ...forecastFilterShape,
  })
  .refine(isMonthRangeOrdered, MONTH_RANGE_ERROR);
export type RollingForecastActiveQuery = z.infer<typeof rollingForecastActiveQuerySchema>;

/** Eine Seite aktiver Projekte mit editierbarer Fertigstellung (paginiert). */
export const rollingForecastActiveProjectsPageSchema = z.object({
  currency: z.enum(['EUR', 'USD', 'CHF']),
  data: z.array(rollingForecastProjectSchema),
  meta: paginationMetaSchema,
});
export type RollingForecastActiveProjectsPage = z.infer<typeof rollingForecastActiveProjectsPageSchema>;
